import { useEffect, useRef } from "react";

/**
 * setInterval that ONLY ticks while:
 *   - the target element is intersecting the viewport (IntersectionObserver)
 *   - the browser tab is visible (Page Visibility API)
 *
 * Decorative "living demo" timers running on every page unconditionally cause
 * React re-renders on the main thread — which is felt as scroll jank on
 * mobile / lower-end devices. Gating them on visibility eliminates that CPU
 * pressure during scroll and completely stops the timer when the section is
 * off-screen or the user has switched tabs.
 *
 * Usage:
 *   const ref = useRef(null);
 *   useIntervalWhenVisible(ref, () => setIdx(v => v+1), 1600);
 *   return <div ref={ref}>...</div>;
 */
export function useIntervalWhenVisible(ref, callback, delayMs) {
    // keep the latest callback in a ref so we don't need to re-arm the timer
    // on every render (would defeat the purpose)
    const savedCallback = useRef(callback);
    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    useEffect(() => {
        if (delayMs == null) return;
        const el = ref.current;
        if (!el || typeof window === "undefined") return;

        // Also skip entirely if the user prefers reduced motion.
        if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            return;
        }

        let timerId = null;
        let inView = false;
        let tabVisible = !document.hidden;

        const tick = () => {
            if (savedCallback.current) savedCallback.current();
        };
        const ensureRunning = () => {
            if (timerId == null && inView && tabVisible) {
                timerId = setInterval(tick, delayMs);
            }
        };
        const ensureStopped = () => {
            if (timerId != null) {
                clearInterval(timerId);
                timerId = null;
            }
        };

        const io = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    inView = entry.isIntersecting;
                    if (inView && tabVisible) ensureRunning();
                    else ensureStopped();
                }
            },
            { threshold: 0.15 },
        );
        io.observe(el);

        const onVisibilityChange = () => {
            tabVisible = !document.hidden;
            if (inView && tabVisible) ensureRunning();
            else ensureStopped();
        };
        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            io.disconnect();
            document.removeEventListener("visibilitychange", onVisibilityChange);
            ensureStopped();
        };
    }, [ref, delayMs]);
}
